import {
  SerializedInventoryLocation,
  SerializedInventoryMeter,
  SerializedInventoryMeterReadingGroup,
} from '@enmon/commons/lib/tools';
import { SortOrder } from 'antd/lib/table/interface';
import { DEFAULT_PAGINATION, ReadingGroupListPagination } from 'routes/reading_groups/constants';
import { StrapiApiResponse } from 'types/strapiResponse';
import { Nullable, StrictOmit } from '@enmon/commons';
import { Authenticator } from '../httpClient';
import { BaseApiClient } from './baseApiClient';

export type ReadingGroupFilter = {
  id?: SerializedInventoryMeterReadingGroup['id'];
  name?: SerializedInventoryMeterReadingGroup['name'];
  location?: number;
  sortOrder?: SortOrder;
  sortField?: string;
};

export interface FindInventoryMeterReadingGroupArgs {
  /** Filter by a single inventory meter id */
  filter?: ReadingGroupFilter & { inventoryMeter?: Nullable<SerializedInventoryMeter['id']> };
  pagination?: StrictOmit<ReadingGroupListPagination, 'total'>;
  signal?: AbortSignal;
}

export type FindInventoryMeterReadingGroupResponse = StrapiApiResponse<SerializedInventoryMeterReadingGroup>;

export type CreateInventoryMeterReadingGroupData = Pick<
  SerializedInventoryMeterReadingGroup,
  'name' | 'description' | 'technology'
> & {
  inventory_location: SerializedInventoryLocation['id'];
};

export type CreateInventoryMeterReadingGroupResponse = { data: SerializedInventoryMeterReadingGroup };

export type UpdateInventoryMeterReadingGroupData = Partial<CreateInventoryMeterReadingGroupData> & {
  inventory_meters?: SerializedInventoryMeter['id'][];
};

export interface DeleteInventoryMeterReadingGroupArgs {
  id: SerializedInventoryMeterReadingGroup['id'];
}

export type DeleteInventoryMeterReadingGroupResponse = SerializedInventoryMeterReadingGroup;

/**
 * API client for managing inventory meter reading groups.
 */
export class InventoryMeterReadingGroupsApiClient extends BaseApiClient<SerializedInventoryMeterReadingGroup> {
  constructor(authenticator: Authenticator | undefined) {
    super('inventory-meter-reading-groups', authenticator);
  }

  async find({
    filter = {},
    pagination = DEFAULT_PAGINATION,
    signal,
  }: FindInventoryMeterReadingGroupArgs = {}): Promise<FindInventoryMeterReadingGroupResponse> {
    const { inventoryMeter, id, name, sortField, sortOrder } = filter;
    const { current, pageSize } = pagination;

    let sort: string | undefined;
    if (sortField && sortOrder) {
      const sortDirection = sortOrder === 'descend' ? 'desc' : 'asc';
      sort = `${sortField}:${sortDirection}`;
    }

    return await this.http.get<FindInventoryMeterReadingGroupResponse>('', {
      params: {
        filters: {
          ...(inventoryMeter && { inventory_meters: { id: { $eq: inventoryMeter } } }),
          ...(id && { id: { $eq: id } }),
          ...(name && { name: { $containsi: name } }),
          ...(filter.location && { inventory_location: { id: { $eq: filter.location } } }),
        },
        populate: { inventory_meters: { fields: ['id'] } },
        ...(sort && { sort }),
        ...(current && { pagination: { page: current, pageSize } }),
      },
      signal,
    });
  }

  async createOne(
    body: CreateInventoryMeterReadingGroupData,
    signal?: AbortSignal,
  ): Promise<SerializedInventoryMeterReadingGroup> {
    const response = await this.http.post<CreateInventoryMeterReadingGroupResponse>('', { data: body }, { signal });
    return response.data;
  }
}

let instance: InventoryMeterReadingGroupsApiClient;

/**
 * Returns a singleton instance of InventoryMeterReadingGroupsApiClient.
 */
export const getInventoryMeterReadingGroupsApiClient = (): InventoryMeterReadingGroupsApiClient =>
  instance ?? (instance = new InventoryMeterReadingGroupsApiClient(undefined));
